/**
 * Fila de notificações de automação com detecção automática de modais (SweetAlert2).
 *
 * Esta fila existe especificamente para as notificações de automação (ex: eventos
 * de background/automáticos do sistema, como atualizações que não partem de uma
 * ação direta do usuário). Ela NÃO se aplica a Swal de confirmação/feedback comuns
 * (ex: "Deseja alterar o status?", "Salvo com sucesso") — esses continuam sendo
 * disparados normalmente via Swal.fire(), fora dessa fila.
 *
 * O objetivo é garantir que uma notificação de automação nunca sobreponha ou seja
 * sobreposta por um Swal padrão disparado pela ação do usuário. Para isso, usa um
 * MutationObserver que monitora o DOM e detecta quando um Swal (identificado pela
 * classe .swal2-container) está visível. Enquanto houver um Swal "real" (não de
 * automação) aberto, as notificações de automação ficam em fila.
 *
 * Toda notificação de automação enfileirada aguarda um delay antes de ser exibida,
 * garantindo que Swal de feedback (que abrem logo após uma request do usuário) não
 * sejam substituídos por ela.
 *
 * Também detecta o caso inverso: se a notificação de automação já estiver aberta e
 * um Swal padrão disparar depois, atropelando-a (o SweetAlert2 só permite uma
 * instância ativa por vez), o item é reenfileirado automaticamente.
 *
 * Importante: para que a detecção funcione, toda notificação de automação deve ser
 * criada com a classe .swal-titulo-notificacao-automacao no título, para que a fila
 * consiga diferenciá-la de um Swal padrão do sistema.
 *
 * Uso:
 *   import notificationQueue from '../../helpers/notificationQueue';
 *
 *   // Enfileirar notificação de automação (será exibida quando não houver
 *   // Swal padrão aberto)
 *   notificationQueue.enqueue(data, callback);
 */

const PROCESS_DELAY = 1500; // ms para aguardar antes de exibir a notificação

class NotificationQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
    this.currentItem = null; // item atualmente em exibição, se houver
    this.observer = null;
    this.processTimer = null;
    this.initObserver();
  }

  /**
   * Inicializa o MutationObserver para detectar Swal modais no DOM.
   */
  initObserver() {
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }

    this.observer = new MutationObserver(() => {
      this.onDomChange();
    });

    const startObserving = () => {
      if (document.body) {
        this.observer.observe(document.body, {
          childList: true,
          subtree: false,
        });
      } else {
        document.addEventListener('DOMContentLoaded', () => {
          this.observer.observe(document.body, {
            childList: true,
            subtree: false,
          });
        });
      }
    };

    startObserving();
  }

  /**
   * Chamado pelo MutationObserver quando há mudança no DOM.
   *
   * Cobre dois cenários:
   * 1. Swal externo apareceu enquanto a fila estava esperando -> cancela
   *    processamento pendente (comportamento original).
   * 2. Swal externo apareceu e "atropelou" a notificação de automação que
   *    já estava em exibição -> reenfileira o item perdido.
   */
  onDomChange() {
    const otherSwalVisible = this.hasSwalVisible();
    const automationVisible = this.isAutomationVisible();

    // Cenário 2: a automação estava sendo exibida e desapareceu no mesmo
    // instante em que um Swal "real" apareceu — foi atropelada.
    if (this.processing && this.currentItem && !automationVisible && otherSwalVisible) {
      this.queue.unshift({
        data: this.currentItem.data,
        callback: this.currentItem.callback,
      });
      this.processing = false;
      this.currentItem = null;
    }

    if (otherSwalVisible) {
      // Cenário 1: Swal externo visível — cancela processamento pendente
      this.cancelProcess();
    } else if (this.queue.length > 0 && !this.processing && !this.processTimer) {
      // Swal externo fechou e há itens na fila — agenda processamento
      this.scheduleProcess();
    }
  }

  /**
   * Verifica se há Swal visível (excluindo o da notificação de automação) no DOM.
   * Esta é a fonte de verdade para decidir se pode processar a fila.
   */
  hasSwalVisible() {
    const swalContainers = document.querySelectorAll('.swal2-container');
    return Array.from(swalContainers).some((container) => {
      const isNotificacaoAutomacao = container.querySelector('.swal-titulo-notificacao-automacao');
      return !isNotificacaoAutomacao;
    });
  }

  /**
   * Verifica se a notificação de automação está atualmente visível no DOM.
   */
  isAutomationVisible() {
    const swalContainers = document.querySelectorAll('.swal2-container');
    return Array.from(swalContainers).some((container) =>
      container.querySelector('.swal-titulo-notificacao-automacao')
    );
  }

  /**
   * Agenda o processamento da fila com delay.
   * Se um Swal abrir nesse intervalo, o observer cancela o timer.
   */
  scheduleProcess() {
    this.cancelProcess();
    this.processTimer = setTimeout(() => {
      this.processTimer = null;
      // Re-verifica o DOM antes de processar
      if (!this.hasSwalVisible()) {
        this.processQueue();
      }
    }, PROCESS_DELAY);
  }

  /**
   * Cancela o processamento pendente.
   */
  cancelProcess() {
    if (this.processTimer) {
      clearTimeout(this.processTimer);
      this.processTimer = null;
    }
  }

  /**
   * Adiciona uma notificação na fila.
   * Sempre agenda com delay — nunca processa imediatamente.
   *
   * @param {object} data - Dados da notificação (vindos do broadcast)
   * @param {function} callback - Função a ser chamada com os dados quando for a vez de exibir
   */
  enqueue(data, callback) {
    this.queue.push({ data, callback });
    if (!this.hasSwalVisible()) {
      this.scheduleProcess();
    }
  }

  /**
   * Processa a fila: exibe a próxima notificação se não há Swal visível.
   * Aguarda o callback finalizar antes de processar o próximo item.
   */
  processQueue() {
    if (this.queue.length === 0 || this.processing || this.hasSwalVisible()) {
      return;
    }

    this.processing = true;
    const item = this.queue.shift();
    this.currentItem = item;

    try {
      const result = item.callback(item.data);
      if (result && typeof result.then === 'function') {
        result.finally(() => this.finishCurrent(item));
      } else {
        this.finishCurrent(item);
      }
    } catch (e) {
      console.error('[NotificationQueue] Erro ao processar notificação:', e);
      this.finishCurrent(item);
    }
  }

  /**
   * Finaliza o processamento do item atual.
   * Guard contra dupla execução: se o item já foi reenfileirado por
   * interrupção (onDomChange detectou atropelamento), this.currentItem
   * já não é mais o mesmo item, então esta chamada tardia é ignorada.
   */
  finishCurrent(item) {
    if (this.currentItem !== item) return;
    this.processing = false;
    this.currentItem = null;
    if (this.queue.length > 0) {
      this.scheduleProcess();
    }
  }
}

const notificationQueue = new NotificationQueue();
export default notificationQueue;